/**
 * Accès réseau et état de connexion.
 *
 * `navigator.onLine` ment : il indique qu'une interface réseau existe, pas que
 * l'API répond — exactement le cas du wifi de salle saturé. On s'appuie donc sur
 * le résultat réel des appels, et sur l'en-tête `x-eventmatch-cache` posé par le
 * service worker quand il sert une réponse du cache.
 */
import { useEffect, useState } from 'react';

export type Reponse<T> = {
  donnees: T | null;
  erreur: string | null;
  depuisLeCache: boolean;
};

export async function appelerApi<T>(chemin: string, options?: RequestInit): Promise<Reponse<T>> {
  try {
    const reponse = await fetch(chemin, {
      ...options,
      headers: {
        // L'en-tête JSON n'accompagne qu'un corps réel : Fastify rejette (à
        // raison) un `content-type: application/json` sans corps.
        ...(options?.body ? { 'content-type': 'application/json' } : {}),
        ...(options?.headers ?? {}),
      },
    });

    const depuisLeCache = reponse.headers.get('x-eventmatch-cache') === 'hors-ligne';

    if (!reponse.ok) {
      const corps = (await reponse.json().catch(() => null)) as
        | { erreur?: { message?: string } }
        | null;
      return {
        donnees: null,
        erreur: corps?.erreur?.message ?? `Erreur ${reponse.status}`,
        depuisLeCache,
      };
    }

    return { donnees: (await reponse.json()) as T, erreur: null, depuisLeCache };
  } catch {
    return { donnees: null, erreur: 'Connexion indisponible', depuisLeCache: false };
  }
}

/** Raccourci pour les envois JSON (POST/PUT), qui dominent l'écriture. */
export async function envoyer<T>(
  chemin: string,
  corps: unknown,
  methode: 'POST' | 'PUT' | 'DELETE' = 'POST',
): Promise<Reponse<T>> {
  return appelerApi<T>(chemin, { method: methode, body: JSON.stringify(corps) });
}

/**
 * Variante pour les routes dont un statut d'erreur porte quand même une réponse
 * utile — `/sante` répond 503 avec le détail de ce qui ne va pas, et c'est
 * précisément ce qu'on veut afficher.
 */
export async function appelerApiTolerant<T>(chemin: string): Promise<T | null> {
  try {
    const reponse = await fetch(chemin);
    return (await reponse.json()) as T;
  } catch {
    return null;
  }
}

/** Vrai tant que le dernier appel a abouti ou que le navigateur se dit connecté. */
export function useEtatConnexion(): { enLigne: boolean; signalerEchec: () => void; signalerSucces: () => void } {
  const [enLigne, setEnLigne] = useState(() => navigator.onLine);

  useEffect(() => {
    const monter = () => setEnLigne(true);
    const descendre = () => setEnLigne(false);
    window.addEventListener('online', monter);
    window.addEventListener('offline', descendre);
    return () => {
      window.removeEventListener('online', monter);
      window.removeEventListener('offline', descendre);
    };
  }, []);

  return {
    enLigne,
    signalerEchec: () => setEnLigne(false),
    signalerSucces: () => setEnLigne(true),
  };
}
